import { useCallback } from 'react';

import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import {
  isCookingApplianceProduct,
  updateShapeByTranslation,
} from '~/core/helpers';
import useAppliance from '~/core/hooks/useAppliance';
import { ApplianceType, DrawingAppliance, DrawingShape } from '~/core/models';
import { ApplianceQtyType } from '~/core/types/appliances';
import { cloneDrawingShape, isRange } from '~/core/utils';
import {
  useDrawingShapeContext,
  usePricedProductsContext,
  useUndoStackContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  resetSelectedBrowsingOptionAndCookingApplianceId,
  resetSelectedCookingAndRanges,
  syncCookingApplianceOnShape,
} from '~/redux/slices/cookingAppliance';
import { resetSelectedCornerRadius } from '~/redux/slices/cornerRadii';
import {
  decreaseCustomCutouts,
  selectCustomCutouts,
  setApplianceCutouts,
  setCustomCutouts,
} from '~/redux/slices/cutout';
import { resetSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { resetSelectedEdge } from '~/redux/slices/edge';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import {
  resetCustomLaborOptionalItems,
  resetOptionalItems,
  resetSelectedWarrantyProduct,
} from '~/redux/slices/optionalItems';
import {
  resetSelectedPredefinedShape,
  updateSelectedDrawingTab,
} from '~/redux/slices/shape';
import {
  resetAllSinkQuantities,
  resetSelectedSink,
  resetSelectedBrowsingOption as resetSelectedSinkBrowsingOption,
} from '~/redux/slices/sink';
import { resetSelectedTearOuts } from '~/redux/slices/tearOut';

export const useToolbarHelper = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);

  const {
    getters: {
      drawingShapes,
      activeDrawingShape,
      areThereShapesInTheDrawingArea,
    },
    handlers: {
      updateActiveDrawingShapeId,
      updateDrawingShapes,
      autoPlaceShapeTranslation,
    },
  } = useDrawingShapeContext();

  const { clearUndoStack } = useUndoStackContext();

  const { resetAllPricing } = usePricedProductsContext();

  const customCutouts = useAppSelector(selectCustomCutouts).selected;
  const { syncCookingAppliancesCount } = useAppliance();

  const duplicateShape = useCallback(
    (shapeIndex: number) => {
      if (!areThereShapesInTheDrawingArea) {
        return;
      }

      const shape = drawingShapes[shapeIndex];
      const duplicatedShape = cloneDrawingShape({
        isCopying: true,
        skipConstraints: false,
      })(shape);

      // Use auto-placement to find a non-overlapping position
      const shapePolygon = duplicatedShape.highlightPoints ?? [];
      const destVector = autoPlaceShapeTranslation(shapePolygon);

      updateShapeByTranslation(destVector, duplicatedShape);

      const updatedDrawingShapes = [...drawingShapes, duplicatedShape];

      updateDrawingShapes(updatedDrawingShapes);

      duplicatedShape.appliances.forEach((appliance: DrawingAppliance) => {
        const { productId, applianceType } = appliance.applianceModel;

        if (isCookingApplianceProduct(appliance.applianceModel)) {
          const selectedType = isRange({ applianceType })
            ? 'selectedRangeApplianceProduct'
            : 'selectedCooktopApplianceProduct';

          dispatch(
            syncCookingApplianceOnShape({
              selectedType,
              applianceId: productId,
              count: 1,
            })
          );
        }
      });

      updateActiveDrawingShapeId(duplicatedShape.id);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      areThereShapesInTheDrawingArea,
      autoPlaceShapeTranslation,
      dispatch,
      drawingShapes,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
    ]
  );

  const resetSelectedCardsOptions = useCallback(() => {
    dispatch(resetSelectedEdge());
    dispatch(resetSelectedCornerRadius());
    dispatch(resetAllSinkQuantities());
    dispatch(resetSelectedTearOuts());
    dispatch(resetOptionalItems());
    dispatch(resetCustomLaborOptionalItems());
    dispatch(resetSelectedWarrantyProduct());
    dispatch(setCustomCutouts());
    dispatch(setApplianceCutouts());
    dispatch(resetSelectedCookingAndRanges());
  }, [dispatch]);

  const cleanupCutoutsOnActiveShape = useCallback(
    (shapeToCleanup?: DrawingShape) => {
      // Use the provided shape or fall back to activeDrawingShape
      // This ensures we're cleaning up the correct shape's appliances
      // even if the context reference is stale
      const shape = shapeToCleanup ?? activeDrawingShape;

      if (!shape) {
        return;
      }

      // Clean up appliances associated with the shape being removed
      // IMPORTANT: We only clean up cutouts here. For sinks and cooking appliances,
      // we DON'T decrease quantities because the appliances might have been moved
      // to other shapes. Instead, we'll sync counts after the shape is removed.
      // Note: Appliances that were moved from this shape to another shape
      // are no longer in this shape's appliances array, so they won't be processed here
      shape.appliances.forEach((appliance) => {
        const { productId, applianceType } = appliance.applianceModel;

        // Handle cutouts that are directly appliances (like electrical cutouts)
        const cutoutsOnShape = [
          ApplianceType.ELECTRICAL_CUTOUT,
          ApplianceType.FAUCET,
          ApplianceType.FAUCET_CUTOUT,
        ];

        if (cutoutsOnShape.includes(applianceType)) {
          // Decrease custom cutouts for electrical cutouts and faucet cutouts
          const currentCutout = customCutouts[productId];

          if (currentCutout?.onShape > 0) {
            dispatch(
              decreaseCustomCutouts({
                cutoutId: productId,
                qtyType: ApplianceQtyType.onShape,
              })
            );
          }
        }
        // NOTE: We do NOT decrease sink or cooking appliance quantities here
        // because the appliances might have been moved to other shapes.
        // Instead, we'll sync counts after the shape is removed.
      });
    },
    [activeDrawingShape, customCutouts, dispatch]
  );

  const removeActiveShape = useCallback(
    (updatedDrawingShapes: DrawingShape[] = []) => {
      if (
        !activeDrawingShape ||
        updatedDrawingShapes.length === drawingShapes.length
      ) {
        return false;
      }

      activeDrawingShape?.onRename?.unsubscribe();
      dispatch(resetSelectedDiagonalCorner());
      dispatch(resetSelectedBrowsingOptionAndCookingApplianceId());
      dispatch(resetSelectedSink());
      dispatch(resetSelectedSinkBrowsingOption());

      if (updatedDrawingShapes.length > 1) {
        updateActiveDrawingShapeId(updatedDrawingShapes[1].id);
        updateDrawingShapes([...updatedDrawingShapes]);

        // Sync cooking appliance counts with what's actually on the remaining shapes
        // This ensures quantities match reality after a shape is removed
        // NOTE: Sinks don't need explicit syncing because `selectedSinksInDrawing` is computed
        // from `drawingShapes` via `getPricedSinksFromDrawingShapes` and automatically
        // recalculates when `drawingShapes` changes. Cooking appliances need explicit sync
        // because their Redux state stores quantities separately and can become out of sync.
        syncCookingAppliancesCount();
      } else {
        // remove all shapes and everything else
        resetSelectedCardsOptions();
        drawingShapes[FAUX_SHAPE_INDEX].appliances = [];
        drawingShapes.splice(1, drawingShapes.length - 1);
        updateDrawingShapes([...drawingShapes]);
        clearUndoStack();
        dispatch(resetSelectedPredefinedShape());
        dispatch(
          updateSelectedDrawingTab(DrawingTabEnum.MODIFY_PREDEFINED_SHAPE_TAB)
        );
        updateActiveDrawingShapeId(undefined);
        resetAllPricing(); // resets area qty to 0
        drawingTool.clearCanvas({ canvas });
      }
      dispatch(updateNeedRedrawCanvas(true));

      return true;
    },
    [
      activeDrawingShape,
      drawingShapes,
      dispatch,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
      canvas,
      clearUndoStack,
      resetAllPricing,
      resetSelectedCardsOptions,
      syncCookingAppliancesCount,
    ]
  );

  return {
    duplicateShape,
    removeActiveShape,
    cleanupCutoutsOnActiveShape,
  };
};
